import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { StorageService } from 'src/app/services/storage.service';
import { ReleaseNotesService } from 'src/app/services/release-notes.service';
import { NotificationsService } from 'src/app/services/notifications.service';

@Component({
  selector: 'app-readers-modal',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./readers-modal.component.scss'],
  template: `
    <div class="rm">

      <div class="rm-accent-bar"></div>

      <!-- Header -->
      <div class="rm-header">
        <div class="rm-header-left">
          <div class="rm-icon-badge">
            <ion-icon name="people-outline"></ion-icon>
          </div>
          <div>
            <h2 class="rm-title">Read Status</h2>
            <p class="rm-subtitle" [title]="notifTitle">{{ notifTitle }}</p>
          </div>
        </div>
        <button class="rm-close" (click)="dismiss()">
          <ion-icon name="close-outline"></ion-icon>
        </button>
      </div>

      <!-- Stats -->
      <div class="rm-stats">
        <div class="rm-stat rm-stat--read">
          <ion-icon name="checkmark-circle"></ion-icon>
          <span class="rm-stat-num">{{ readers.length }}</span>
          <span class="rm-stat-label">Read</span>
        </div>
        <div class="rm-stat rm-stat--unread">
          <ion-icon name="time-outline"></ion-icon>
          <span class="rm-stat-num">{{ notReaders.length }}</span>
          <span class="rm-stat-label">Not read</span>
        </div>
      </div>

      <!-- Segment -->
      <div class="rm-segment">
        <button class="rm-seg-btn" [class.active]="tab === 'read'" (click)="tab = 'read'">
          <ion-icon name="checkmark-circle-outline"></ion-icon>
          Read ({{ readers.length }})
        </button>
        <button class="rm-seg-btn" [class.active]="tab === 'unread'" (click)="tab = 'unread'">
          <ion-icon name="time-outline"></ion-icon>
          Not read ({{ notReaders.length }})
        </button>
      </div>

      <!-- Search -->
      <div class="rm-search">
        <ion-icon name="search-outline" class="rm-search-icon"></ion-icon>
        <input type="text" [(ngModel)]="searchQuery" placeholder="Search by name..." class="rm-search-input" />
      </div>

      <!-- Content -->
      <div class="rm-scroll">

        <div *ngIf="loading" class="rm-loading">
          <ion-spinner name="crescent"></ion-spinner>
        </div>

        <!-- Read Tab -->
        <div *ngIf="!loading && tab === 'read'">
          <div *ngIf="filteredReaders.length === 0" class="rm-empty">
            <ion-icon name="checkmark-done-outline"></ion-icon>
            <p>{{ searchQuery ? 'No matches found' : 'No one has read this yet' }}</p>
          </div>
          <div *ngFor="let u of filteredReaders" class="rm-user-row">
            <div class="rm-avatar">{{ getInitials(u) }}</div>
            <div class="rm-user-info">
              <span class="rm-user-name">{{ u.name }} {{ u.surname }}</span>
              <span class="rm-user-branch" *ngIf="u.branch">{{ u.branch }}</span>
            </div>
            <span class="rm-read-time">{{ formatDate(u.readAt) }}</span>
          </div>
        </div>

        <!-- Unread Tab -->
        <div *ngIf="!loading && tab === 'unread'">
          <div *ngIf="filteredNotReaders.length === 0" class="rm-empty">
            <ion-icon name="checkmark-done-circle-outline"></ion-icon>
            <p>{{ searchQuery ? 'No matches found' : 'Everyone has read this!' }}</p>
          </div>
          <div *ngFor="let u of filteredNotReaders" class="rm-user-row">
            <div class="rm-avatar rm-avatar--pending">{{ getInitials(u) }}</div>
            <div class="rm-user-info">
              <span class="rm-user-name">{{ u.name }} {{ u.surname }}</span>
              <span class="rm-user-branch" *ngIf="u.branch">{{ u.branch }}</span>
            </div>
            <span class="rm-pending-badge">Pending</span>
          </div>
        </div>

      </div>

    </div>
  `
})
export class ReadersModalComponent implements OnInit {
  @Input() notifKey: number;
  @Input() notifTitle: string = '';
  @Input() service: 'release-notes' | 'notifications' = 'notifications';

  loading = false;
  tab: 'read' | 'unread' = 'read';
  searchQuery = '';

  readers: any[] = [];
  notReaders: any[] = [];

  constructor(
    private modalCtrl: ModalController,
    private storageService: StorageService,
    private releaseNotesService: ReleaseNotesService,
    private notificationsService: NotificationsService
  ) {}

  ngOnInit() {
    this.loadReaders();
  }

  loadReaders() {
    this.loading = true;
    const user = this.storageService.getUserStorage();
    const userKey = user?.id;

    const obs = this.service === 'release-notes'
      ? this.releaseNotesService.adminGetReaders(userKey, this.notifKey)
      : this.notificationsService.adminGetReaders(userKey, this.notifKey);

    obs.subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.readers = res.data.readers || [];
          this.notReaders = res.data.notReaders || [];
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('[READERS_MODAL] API error:', err);
        this.loading = false;
      }
    });
  }

  get filteredReaders(): any[] {
    return this.filterBySearch(this.readers);
  }

  get filteredNotReaders(): any[] {
    return this.filterBySearch(this.notReaders);
  }

  private filterBySearch(list: any[]): any[] {
    if (!this.searchQuery.trim()) return list;
    const q = this.searchQuery.toLowerCase();
    return list.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.surname || '').toLowerCase().includes(q) ||
      (u.branch || '').toLowerCase().includes(q)
    );
  }

  getInitials(u: any): string {
    return ((u.name?.[0] || '') + (u.surname?.[0] || '')).toUpperCase();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
